import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, Grid, Box, Paper, Chip, Alert, CircularProgress,
  FormControl, Select, MenuItem, TextField, Button, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, SxProps, Theme, TableContainer
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteIcon from "@mui/icons-material/Delete";
import { getUserFriendlyError } from '../utils/errorHandler';
import AccountPickerSingle from '../components/AccountPickerSingle';

interface Account {
  id: string;
  name: string;
  phone_number: string;
}

interface ActiveCopywriter {
  task_id: string;
  account_name: string;
  source_channels: string[];
  target_channel: string;
  rewrite_text: boolean;
  sys_prompt?: string;
  tone?: string;
  status: string;
  started_at?: string;
  total_messages_received: number;
  total_messages_copied: number;
  total_messages_filtered: number;
  total_messages_failed: number;
}

interface CopywritingForm {
  accountName: string;
  sourceChannels: string;
  targetChannel: string;
  rewriteText: boolean;
  sysPrompt: string;
  tone: string;
  minTextLength: string;
  maxLinksCount: string;
  filterKeywords: string;
  excludeKeywords: string;
}

const NeuroCopywriting: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [activeJobs, setActiveJobs] = useState<ActiveCopywriter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [tab, setTab] = useState<'setup' | 'active'>('setup');

  const [form, setForm] = useState<CopywritingForm>({
    accountName: '',
    sourceChannels: '',
    targetChannel: '',
    rewriteText: true,
    sysPrompt: '',
    tone: '',
    minTextLength: '0',
    maxLinksCount: '10',
    filterKeywords: '',
    excludeKeywords: '',
  });
  const isStartDisabled = !form.accountName || !form.sourceChannels.trim() || !form.targetChannel.trim();
  const update = (key: keyof CopywritingForm, value: string | boolean) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    const interval = setInterval(fetchActive, 5000);
    return () => clearInterval(interval);
  }, [auth?.isAuthenticated, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [accRes, jobsRes] = await Promise.all([
        api.get('/accounts'),
        api.get('/copywrite/list')
      ]);
      setAccounts(accRes.data.accounts || []);
      setActiveJobs(jobsRes.data || []);
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка загрузки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActive = async () => {
    try {
      const res = await api.get('/copywrite/list');
      setActiveJobs(res.data || []);
    } catch (err) {
      console.error('Ошибка обновления задач:', err);
    }
  };

  const start = async () => {
    if (!form.accountName || !form.sourceChannels || !form.targetChannel) {
      setMessage({ text: 'Заполните обязательные поля', type: 'error' });
      return;
    }
    setIsLoading(true);
    const src = form.sourceChannels.split(',').map(s => s.trim()).filter(Boolean);
    const inc = form.filterKeywords.split(',').map(s => s.trim()).filter(Boolean);
    const exc = form.excludeKeywords.split(',').map(s => s.trim()).filter(Boolean);
    try {
      await api.post('/copywrite/start', {
        account_name: form.accountName,
        source_channels: src,
        target_channel: form.targetChannel,
        rewrite_text: form.rewriteText,
        sys_prompt: form.sysPrompt || null,
        tone: form.tone || null,
        min_text_length: Number(form.minTextLength) || 0,
        max_links_count: Number(form.maxLinksCount) || 10,
        filter_keywords: inc,
        exclude_keywords: exc,
      });
      setMessage({ text: 'Нейрокопирайтер запущен!', type: 'success' });
      setTab('active');
      fetchActive();
      update('sourceChannels', '');
      update('targetChannel', '');
    } catch (err: any) {
  const text = getUserFriendlyError(err, accounts, 'Нейрокопирайтер');
  // или 'накрутку реакций', 'парсинг', 'восстановку задачи' — как хочешь
  setMessage({ text, type: 'error' });
} finally {
      setIsLoading(false);
    }
  };

  const stop = async (taskId: string) => {
    setIsLoading(true);
    try {
      await api.get(`/copywrite/${taskId}/stop`);
      setMessage({ text: 'Остановлено', type: 'info' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка остановки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const gridItem: SxProps<Theme> = { display: 'flex', alignItems: 'center', width: '100%' };

  // === СТИЛИ ВКЛАДОК С ДОСТУПОМ К `tab` ===
  const getTabStyle = (current: 'setup' | 'active') => ({
    flex: 1,
    py: 2,
    borderRadius: 0,
    bgcolor: tab === current ? '#E0F2FE' : 'transparent',
    color: tab === current ? '#1E40AF' : '#6B7280',
    fontWeight: tab === current ? 600 : 400,
    '&:hover': { bgcolor: tab === current ? '#DBEAFE' : '#F3F4F6' }
  });

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* === КРАТКОЕ + ЧИПЫ === */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#F8FAFC', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <AutorenewIcon sx={{ fontSize: 40, color: '#1E40AF' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A8A', mb: 1 }}>
              Нейрокопирайтинг
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="AI-переписывание" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 500 }} />
              <Chip label="Ведение каналов" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 500 }} />
              <Chip label="Агрегация контента" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 500 }} />
              <Chip label="Получение из закрытых источников" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 500 }} />
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Копирует контент из других каналов в ваш — с AI-переписыванием и механизмами фильтрации.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* === ПОДРОБНОЕ ОПИСАНИЕ === */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как работает нейрокопирайтинг?
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Аккаунт автоматически копирует сообщения из указанных каналов-источников в ваш целевой канал.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Копирование с переписыванием</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Текст переписывается через AI (по промпту и тону), или получается в исходном виде.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Полная поддержка медиа</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Фото, видео, альбомы, кастомные эмодзи — всё копируется автоматически, даже при запрете на перессылку и скачивание
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Гибкая фильтрация</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              По длине текста, количеству ссылок, ключевым словам (включить/исключить).
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>

        </Box>
         <Typography sx={{ color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic' }}>
          Идеально для получения и агрегации контента с адаптацией под ваш стиль.
        </Typography>
          <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Ознакомьтесь с документацией по автоматизации{' '}
          <Typography
              component="a"
              href="https://typhon.gitbook.io/docs/automatizations_telegram/neurocopywrite"
              sx={{textDecoration: 'underline', cursor: 'pointer'}}
          >
            здесь
          </Typography>
          </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
          <Button
            onClick={() => setTab('setup')}
            sx={getTabStyle('setup')}
            startIcon={<PlayArrowIcon />}
          >
            Настройка
          </Button>
          <Button
            onClick={() => setTab('active')}
            sx={getTabStyle('active')}
            startIcon={<StopIcon />}
          >
            Мои нейрокопирайтеры
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {tab === 'setup' && (
            <SetupTab
              form={form}
              update={update}
              accounts={accounts}
              start={start}
              isLoading={isLoading}
              gridItem={gridItem}
              isStartDisabled={isStartDisabled}
            />
          )}
          {tab === 'active' && (
  <ActiveTab
    jobs={activeJobs}
    stop={stop}
    isLoading={isLoading}
    setMessage={setMessage}     // ← ДОБАВЬ ЭТО
    fetchActive={fetchActive}
    accounts={accounts}
  />
)}
        </Box>
      </Paper>
    </Container>
  );
};

// === Вкладка настройки ===
interface SetupTabProps {
  form: CopywritingForm;
  update: (key: keyof CopywritingForm, value: string | boolean) => void;
  accounts: Account[];
  start: () => void;
  isLoading: boolean;
  gridItem: SxProps<Theme>;
  isStartDisabled: boolean;
}

const SetupTab: React.FC<SetupTabProps> = ({ form, isStartDisabled, update, accounts, start, isLoading, gridItem }) => (
  <Grid container spacing={2}>
    <Grid size={12} sx={gridItem}>
      <AccountPickerSingle
        accounts={accounts}
        selected={form.accountName}
        onChange={(name) => update('accountName', name)}
        placeholder="Выберите аккаунт"
      />
    </Grid>

    <Grid size={12} sx={gridItem}>
      <TextField
        label="Каналы-источники"
        value={form.sourceChannels}
        onChange={(e) => update('sourceChannels', e.target.value)}
        fullWidth
        required
        placeholder="channel1, @channel2"
      />
    </Grid>

    <Grid size={12} sx={gridItem}>
      <TextField
        label="Целевой канал"
        value={form.targetChannel}
        onChange={(e) => update('targetChannel', e.target.value)}
        fullWidth
        required
        placeholder="@my_channel"
      />
    </Grid>

    <Grid size={12} sx={gridItem}>
      <FormControlLabel
        control={
          <Checkbox
            checked={form.rewriteText}
            onChange={(e) => update('rewriteText', e.target.checked)}
          />
        }
        label="Переписывать через AI"
      />
    </Grid>

    {form.rewriteText && (
      <>
        <Grid size={12} sx={gridItem}>
          <TextField
            label="Системный промпт"
            value={form.sysPrompt}
            onChange={(e) => update('sysPrompt', e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Перепиши в деловом стиле..."
          />
        </Grid>
        <Grid size={12} sx={gridItem}>
          <TextField
            label="Тон"
            value={form.tone}
            onChange={(e) => update('tone', e.target.value)}
            fullWidth
            placeholder="профессиональный, дружелюбный"
          />
        </Grid>
      </>
    )}

    <Grid size={6} sx={gridItem}>
      <TextField
        label="Мин. длина текста"
        type="number"
        value={form.minTextLength}
        onChange={(e) => update('minTextLength', e.target.value)}
        fullWidth
      />
    </Grid>
    <Grid size={6} sx={gridItem}>
      <TextField
        label="Макс. ссылок"
        type="number"
        value={form.maxLinksCount}
        onChange={(e) => update('maxLinksCount', e.target.value)}
        fullWidth
      />
    </Grid>

    <Grid size={12} sx={gridItem}>
      <TextField
        label="Включить слова"
        value={form.filterKeywords}
        onChange={(e) => update('filterKeywords', e.target.value)}
        fullWidth
        placeholder="новость, обзор"
      />
    </Grid>
    <Grid size={12} sx={gridItem}>
      <TextField
        label="Исключить слова"
        value={form.excludeKeywords}
        onChange={(e) => update('excludeKeywords', e.target.value)}
        fullWidth
        placeholder="реклама, спам"
      />
    </Grid>

    <Grid size={12}>
      <Button
          variant="contained"
          onClick={start}
          disabled={isLoading || isStartDisabled}
          startIcon={isLoading ? <CircularProgress size={22}/> : <PlayArrowIcon/>}
          fullWidth
          sx={{
            py: 2,
            fontSize: '1.1rem',
            bgcolor: '#1E3A8A',
            '&:hover': {
              bgcolor: '#1E40AF'
            }
          }}
      >
        {isLoading ? 'Запуск...' : 'Запустить'}
      </Button>
    </Grid>
  </Grid>
);

// === Вкладка активных задач ===
interface ActiveTabProps {
  jobs: ActiveCopywriter[];
  stop: (taskId: string) => void;
  isLoading: boolean;
}

const ActiveTab: React.FC<{
  jobs: ActiveCopywriter[];
  stop: (taskId: string) => Promise<void>;
  isLoading: boolean;
  setMessage: (msg: { text: string; type: 'success' | 'error' | 'info' } | null) => void;
  fetchActive: () => Promise<void>;
  accounts: Account[];
}> = ({ jobs, stop, isLoading, setMessage, fetchActive, accounts }) => {
  const navigate = useNavigate();

  const [deleting, setDeleting] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  const handleDelete = async (taskId: string) => {
    if (!window.confirm('Удалить задачу НАВСЕГДА? Это нельзя отменить.')) return;

    setDeleting(taskId);
    try {
      await api.get(`/copywrite/delete/${taskId}`);
      setMessage({ text: 'Задача удалена', type: 'info' });
      await fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка при удалении', type: 'error' });
    } finally {
      setDeleting(null);
    }
  };

  const handleRestore = async (taskId: string) => {
    setRestoring(taskId);
    try {
      await api.get(`/copywrite/restore/${taskId}`);
      setMessage({ text: 'Копирайтер возобновлён!', type: 'success' });
      await fetchActive();
    } catch (err: any) {

    const text = getUserFriendlyError(err, accounts, 'возобновление копирайтера');
    setMessage({ text, type: 'error' });
  } finally {
      setRestoring(null);
    }
  };

  if (jobs.length === 0) {
    return (
      <Typography textAlign="center" py={6} color="text.secondary" variant="h6">
        Нет активных копирайтеров
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Task ID</strong></TableCell>
            <TableCell><strong>Аккаунт</strong></TableCell>
            <TableCell><strong>Источник → Цель</strong></TableCell>
            <TableCell><strong>Получено</strong></TableCell>
            <TableCell><strong>Скопировано</strong></TableCell>
            <TableCell><strong>Фильтр</strong></TableCell>
            <TableCell><strong>Ошибки</strong></TableCell>
            <TableCell><strong>Статус</strong></TableCell>
            <TableCell align="right"><strong>Действия</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job) => (
            <TableRow
              key={job.task_id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/copywriting/detail/${job.task_id}`)}
            >
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {job.task_id.slice(0, 8)}
              </TableCell>
              <TableCell sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                {job.account_name}
              </TableCell>
              <TableCell sx={{ fontSize: '0.85rem' }}>
                {job.source_channels.length > 1
                  ? `${job.source_channels.length} каналов`
                  : job.source_channels[0]}
                {' → '}
                <Box component="span" fontWeight={600}>{job.target_channel}</Box>
              </TableCell>
              <TableCell sx={{color: "#8B5CF6"}}>{job.total_messages_received}</TableCell>
              <TableCell sx={{ color: '#10B981', fontWeight: 'medium' }}>
                {job.total_messages_copied}
              </TableCell>
              <TableCell sx={{ color: '#F59E0B' }}>
                {job.total_messages_filtered}
              </TableCell>
              <TableCell sx={{ color: job.total_messages_failed ? '#DC2626' : 'inherit', fontWeight: 'medium' }}>
                {job.total_messages_failed}
              </TableCell>
              <TableCell>
                <Chip
                  label={
                    job.status === 'running' ? 'Работает' :
                    job.status === 'stopped' ? 'Остановлен' :
                    job.status === 'error' ? 'Ошибка' :
                    job.status === 'dead' ? 'Умер' : job.status
                  }
                  size="small"
                  color={
                    job.status === 'running' ? 'success' :
                    job.status === 'stopped' ? 'warning' :
                    job.status === 'error' || job.status === 'dead' ? 'error' : 'default'
                  }
                />
              </TableCell>

              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>

                  {/* ВОЗОБНОВИТЬ */}
                  {job.status === 'stopped' && (
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleRestore(job.task_id)}
                      disabled={restoring === job.task_id}
                      title="Возобновить копирайтер"
                    >
                      {restoring === job.task_id ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                  )}

                  {/* ОСТАНОВИТЬ */}
                  {job.status === 'running' && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => stop(job.task_id)}
                      disabled={isLoading}
                      title="Остановить"
                    >
                      <StopIcon fontSize="small" />
                    </IconButton>
                  )}

                  {/* УДАЛИТЬ НАВСЕГДА */}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(job.task_id)}
                    disabled={deleting === job.task_id}
                    title="Удалить навсегда"
                  >
                    {deleting === job.task_id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// === Стиль метки ===
const labelStyle: React.CSSProperties = {
  position: 'absolute',
  top: -8,
  left: 14,
  fontSize: '0.75rem',
  color: '#6B7280',
  pointerEvents: 'none',
  backgroundColor: 'white',
  padding: '0 4px'
};

export default NeuroCopywriting;
